import type { PropsWithChildren } from 'react'
import { useEffect, useMemo } from 'react'

import { QueryClientProvider } from '@tanstack/react-query'
import { CssBaseline, StyledEngineProvider, ThemeProvider  } from '@mui/material'
import { SnackbarProvider } from 'notistack'

import { queryClient } from '@/lib/react-query'
import { lightTheme, darkTheme } from '@/theme/themes'
import { useThemeStore } from '@/store/useThemeStore'
import { useSystemTheme } from '@/hooks/useSystemTheme'

import { AuthProvider } from './AuthProvider'
import { PermissionProvider } from '@/contexts/PermissionContext'

export const AppProviders = ({ children }: PropsWithChildren) => {
  const { effectiveMode, setSystemTheme } = useThemeStore()
  const systemTheme = useSystemTheme()

  // Update system theme when it changes
  useEffect(() => {
    setSystemTheme(systemTheme)
  }, [systemTheme, setSystemTheme])

  // Update document attributes for CSS variables
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', effectiveMode)
    document.documentElement.style.colorScheme = effectiveMode
  }, [effectiveMode])

  const currentTheme = useMemo(
    () => (effectiveMode === 'dark' ? darkTheme : lightTheme),
    [effectiveMode],
  )

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={currentTheme}>
        <CssBaseline />
        <SnackbarProvider
          maxSnack={4}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          autoHideDuration={4000}
        >
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <PermissionProvider>{children}</PermissionProvider>
            </AuthProvider>
          </QueryClientProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  )
}
